import type { CurrentSession } from "./currentSession";
import {
  organizationsForCapabilities,
  type OrganizationId,
} from "./accessOrganizations";

export type ProductAreaId =
  | "distribution"
  | "operations"
  | "audit"
  | "administration"
  | "laundry";
export type AppDestinationId = ProductAreaId;
export type AppDestinationBrand = "diaria-brasil" | "facilita";
export type ProductRouteView =
  | "dashboard"
  | "admin-dashboard"
  | "staff-camareira"
  | "lavanderia"
  | "auditoria"
  | "inspecoes"
  | "manutencao"
  | "apps";
export type ProductWorkflow =
  | "distribution"
  | "distribution-schedules"
  | "distribution-alias"
  | "tasks"
  | "maintenance-execution"
  | "maintenance-planning"
  | "governance"
  | "vehicle"
  | "maintenance-audit"
  | "governance-audit"
  | "vehicle-audit"
  | "vehicle-catalog-management"
  | "inspections-audit"
  | "administration"
  | "laundry"
  | "operations-root"
  | "inspections-root-legacy"
  | "maintenance-legacy"
  | "maintenance-locale-legacy"
  | "audit-root";

export type AppDestination = {
  id: AppDestinationId;
  brand: AppDestinationBrand;
  label: string;
  description: string;
  path: string;
};

export type ProductRouteMatch = {
  area: ProductAreaId;
  view: ProductRouteView;
  workflow: ProductWorkflow;
  legacy: boolean;
};

export const CANONICAL_PRODUCT_PATHS = {
  operationTasks: "/operacao/tarefas",
  operationMaintenance: "/operacao/manutencao",
  operationGovernance: "/operacao/governanca",
  operationVehicle: "/operacao/vistoria-veicular",
  auditMaintenance: "/auditoria/manutencao",
  auditGovernance: "/auditoria/governanca",
  auditVehicle: "/auditoria/vistoria-veicular",
  auditVehicleReports: "/auditoria/vistoria-veicular/relatorios",
  auditVehicleCatalog: "/auditoria/vistoria-veicular/veiculos",
} as const;

function hasPermission(session: CurrentSession, permission: string) {
  return session.permissions.includes(permission);
}

function hasOrganization(
  session: CurrentSession,
  organization: OrganizationId,
) {
  if (session.administrative_level === "administrator") return true;
  if (session.user.organizations !== undefined) {
    return session.user.organizations.includes(organization);
  }
  // Compatibility while an older API version may still omit organizations.
  return (
    organizationsForCapabilities(session.permissions).includes(organization) ||
    (organization === "diaria_brasil" &&
      session.authorization.administration_access)
  );
}

function isClient(session: CurrentSession) {
  return session.user.user_type === "client";
}

function normalizedPath(path: string) {
  return (path.split(/[?#]/, 1)[0] || "/").toLowerCase();
}

function matchesPrefix(path: string, prefix: string) {
  return path === prefix || path.startsWith(`${prefix}/`);
}

function matchesExactPath(path: string, expected: string) {
  return path === expected || path === `${expected}/`;
}

function distributionPath(session: CurrentSession) {
  const locale = session.user.locale.toLowerCase();
  const localePrefix = locale === "df" || locale === "sp" ? locale : "df";
  const workflow = hasPermission(session, "distribution.access")
    ? "tarefas"
    : "manutencao";
  return `/${localePrefix}/distribuicao/${workflow}`;
}

export function canExecuteTasks(session: CurrentSession) {
  return hasPermission(session, "operations.tasks.execute");
}

export function canAccessOperations(session: CurrentSession) {
  return Boolean(
    canExecuteTasks(session) ||
      hasPermission(session, "maintenance.execute") ||
      hasPermission(session, "inspections.governance") ||
      hasPermission(session, "inspections.vehicle"),
  );
}

export function canAccessAudit(session: CurrentSession) {
  return Boolean(
    hasPermission(session, "maintenance.audit") ||
      hasPermission(session, "inspections.audit.governance") ||
      hasPermission(session, "inspections.audit.vehicle"),
  );
}

export function canAccessProductArea(session: CurrentSession, area: ProductAreaId) {
  if (isClient(session) && area !== "distribution") return false;
  const organization =
    area === "laundry" ? "facilita_lavanderia" : "diaria_brasil";
  if (!hasOrganization(session, organization)) return false;
  switch (area) {
    case "distribution":
      return Boolean(
        hasPermission(session, "distribution.access") ||
          hasPermission(session, "maintenance.assign"),
      );
    case "operations":
      return canAccessOperations(session);
    case "audit":
      return canAccessAudit(session);
    case "administration":
      return session.authorization.administration_access;
    case "laundry":
      return hasPermission(session, "laundry.access");
  }
}

function firstOperationPath(session: CurrentSession) {
  if (canExecuteTasks(session)) return CANONICAL_PRODUCT_PATHS.operationTasks;
  if (hasPermission(session, "maintenance.execute")) {
    return CANONICAL_PRODUCT_PATHS.operationMaintenance;
  }
  if (hasPermission(session, "inspections.governance")) {
    return CANONICAL_PRODUCT_PATHS.operationGovernance;
  }
  return CANONICAL_PRODUCT_PATHS.operationVehicle;
}

export function firstAuditPath(session: CurrentSession) {
  if (hasPermission(session, "maintenance.audit")) {
    return CANONICAL_PRODUCT_PATHS.auditMaintenance;
  }
  if (hasPermission(session, "inspections.audit.governance")) {
    return CANONICAL_PRODUCT_PATHS.auditGovernance;
  }
  return CANONICAL_PRODUCT_PATHS.auditVehicle;
}

function firstInspectionAuditPath(session: CurrentSession) {
  return hasPermission(session, "inspections.audit.governance")
    ? CANONICAL_PRODUCT_PATHS.auditGovernance
    : CANONICAL_PRODUCT_PATHS.auditVehicle;
}

/** Returns one launcher destination per stable product area. */
export function getAppDestinations(session: CurrentSession): AppDestination[] {
  const destinations: AppDestination[] = [];
  if (canAccessProductArea(session, "distribution")) {
    destinations.push({
      id: "distribution",
      brand: "diaria-brasil",
      label: "Planejamento",
      description: "Planeje a distribuição e a manutenção da operação.",
      path: distributionPath(session),
    });
  }
  if (isClient(session)) return destinations;
  if (canAccessProductArea(session, "operations")) {
    destinations.push({
      id: "operations",
      brand: "diaria-brasil",
      label: "Operação",
      description: "Execute tarefas, manutenções e inspeções em campo.",
      path: firstOperationPath(session),
    });
  }
  if (canAccessProductArea(session, "audit")) {
    destinations.push({
      id: "audit",
      brand: "diaria-brasil",
      label: "Auditoria",
      description: "Revise manutenções, inspeções e suas evidências.",
      path: firstAuditPath(session),
    });
  }
  if (canAccessProductArea(session, "administration")) {
    destinations.push({
      id: "administration",
      brand: "diaria-brasil",
      label: "Administração",
      description: "Gerencie usuários, cadastros e a operação interna.",
      path: "/admin/dashboard",
    });
  }
  if (canAccessProductArea(session, "laundry")) {
    destinations.push({
      id: "laundry",
      brand: "facilita",
      label: "Lavanderia",
      description: "Acesse a operação da Facilita Lavanderia.",
      path: "/lavanderia/dashboard",
    });
  }
  return destinations;
}

export function hasMultipleProductAreas(session: CurrentSession) {
  return getAppDestinations(session).length > 1;
}

/** Recognizes canonical routes and every legacy route kept during migration. */
export function recognizeProductPath(path: string): ProductRouteMatch | null {
  const pathname = normalizedPath(path);
  if (matchesPrefix(pathname, CANONICAL_PRODUCT_PATHS.operationTasks)) {
    return { area: "operations", view: "staff-camareira", workflow: "tasks", legacy: false };
  }
  if (matchesPrefix(pathname, CANONICAL_PRODUCT_PATHS.operationMaintenance)) {
    return { area: "operations", view: "manutencao", workflow: "maintenance-execution", legacy: false };
  }
  if (matchesPrefix(pathname, CANONICAL_PRODUCT_PATHS.operationGovernance)) {
    return { area: "operations", view: "inspecoes", workflow: "governance", legacy: false };
  }
  if (matchesPrefix(pathname, CANONICAL_PRODUCT_PATHS.operationVehicle)) {
    return { area: "operations", view: "inspecoes", workflow: "vehicle", legacy: false };
  }
  if (pathname === "/operacao" || pathname === "/operacao/") {
    return { area: "operations", view: "staff-camareira", workflow: "operations-root", legacy: false };
  }
  if (matchesPrefix(pathname, CANONICAL_PRODUCT_PATHS.auditMaintenance)) {
    return { area: "audit", view: "auditoria", workflow: "maintenance-audit", legacy: false };
  }
  if (matchesPrefix(pathname, CANONICAL_PRODUCT_PATHS.auditGovernance)) {
    return { area: "audit", view: "auditoria", workflow: "governance-audit", legacy: false };
  }
  if (matchesPrefix(pathname, CANONICAL_PRODUCT_PATHS.auditVehicleCatalog)) {
    return { area: "audit", view: "auditoria", workflow: "vehicle-catalog-management", legacy: false };
  }
  if (matchesPrefix(pathname, CANONICAL_PRODUCT_PATHS.auditVehicle)) {
    return { area: "audit", view: "auditoria", workflow: "vehicle-audit", legacy: false };
  }
  if (matchesPrefix(pathname, "/aplicativos")) {
    return { area: "administration", view: "apps", workflow: "administration", legacy: false };
  }
  if (
    matchesExactPath(pathname, "/admin/veiculos") ||
    matchesExactPath(pathname, "/auditoria/veiculos")
  ) {
    return {
      area: "audit",
      view: "auditoria",
      workflow: "vehicle-catalog-management",
      legacy: true,
    };
  }
  if (matchesPrefix(pathname, "/admin") || pathname === "/cadastro") {
    return { area: "administration", view: "admin-dashboard", workflow: "administration", legacy: false };
  }
  if (matchesPrefix(pathname, "/lavanderia")) {
    return { area: "laundry", view: "lavanderia", workflow: "laundry", legacy: false };
  }
  if (/^\/(df|sp)\/distribuicao\/tarefas\/programacoes(?:\/|$)/.test(pathname)) {
    return { area: "distribution", view: "dashboard", workflow: "distribution-schedules", legacy: false };
  }
  if (/^\/(df|sp)\/distribuicao\/tarefas(?:\/historico)?(?:\/|$)/.test(pathname)) {
    return { area: "distribution", view: "dashboard", workflow: "distribution", legacy: false };
  }
  if (/^\/(df|sp)\/distribuicao\/manutencao(?:\/|$)/.test(pathname)) {
    return { area: "distribution", view: "dashboard", workflow: "maintenance-planning", legacy: false };
  }
  if (/^\/(df|sp)\/manutencao\/?$/.test(pathname)) {
    return { area: "distribution", view: "dashboard", workflow: "maintenance-locale-legacy", legacy: true };
  }
  if (/^\/(df|sp)\/tarefas(?:\/historico)?(?:\/|$)/.test(pathname)) {
    return { area: "distribution", view: "dashboard", workflow: "distribution", legacy: true };
  }
  if (
    matchesPrefix(pathname, "/dashboard") ||
    matchesPrefix(pathname, "/distribuicao") ||
    matchesPrefix(pathname, "/historico") ||
    /^\/(df|sp)\/?$/.test(pathname) ||
    /^\/(df|sp)\/(dashboard|distribuicao|historico)\/?$/.test(pathname)
  ) {
    // Atalhos historicos da area, sem workflow proprio: a canonicalizacao
    // resolve entre Tarefas e Manutencao pelas capabilities do usuario.
    return { area: "distribution", view: "dashboard", workflow: "distribution-alias", legacy: true };
  }
  if (
    matchesExactPath(pathname, "/staff/camareira") ||
    matchesExactPath(pathname, "/staff/profissional")
  ) {
    return { area: "operations", view: "staff-camareira", workflow: "tasks", legacy: true };
  }
  if (
    matchesExactPath(pathname, "/manutencao") ||
    matchesExactPath(pathname, "/manutencao/ordens")
  ) {
    return { area: "operations", view: "manutencao", workflow: "maintenance-legacy", legacy: true };
  }
  if (matchesExactPath(pathname, "/inspecoes/vistoria-veicular")) {
    return { area: "operations", view: "inspecoes", workflow: "vehicle", legacy: true };
  }
  if (matchesExactPath(pathname, "/inspecoes/governanca")) {
    return { area: "operations", view: "inspecoes", workflow: "governance", legacy: true };
  }
  if (matchesExactPath(pathname, "/inspecoes")) {
    return { area: "operations", view: "inspecoes", workflow: "inspections-root-legacy", legacy: true };
  }
  if (pathname === "/auditoria" || pathname === "/auditoria/") {
    return { area: "audit", view: "auditoria", workflow: "audit-root", legacy: false };
  }
  if (
    matchesPrefix(pathname, "/auditoria/inspecoes") ||
    matchesExactPath(pathname, "/auditoria/dashboard") ||
    matchesExactPath(pathname, "/auditoria/veicular") ||
    matchesExactPath(pathname, "/auditoria/vistorias-preenchidas") ||
    matchesExactPath(pathname, "/auditoria/governanca-preenchidas") ||
    matchesExactPath(pathname, "/auditoria/controles")
  ) {
    return { area: "audit", view: "auditoria", workflow: "inspections-audit", legacy: true };
  }
  return null;
}

export function canAccessPath(session: CurrentSession, path: string) {
  const route = recognizeProductPath(path);
  if (!route || route.view === "apps") return false;
  // This legacy URL served two distinct workflows. Resolve its capability
  // before applying a product-area predicate so an auditor is not mistaken
  // for an Operations user.
  if (route.workflow === "maintenance-legacy") {
    if (isClient(session)) return false;
    return Boolean(
      hasPermission(session, "maintenance.execute") ||
        hasPermission(session, "maintenance.audit"),
    );
  }
  if (route.workflow === "maintenance-locale-legacy") {
    if (isClient(session)) return false;
    return Boolean(
      hasPermission(session, "maintenance.assign") ||
        hasPermission(session, "maintenance.audit"),
    );
  }
  if (!canAccessProductArea(session, route.area)) return false;
  switch (route.workflow) {
    case "distribution":
      return hasPermission(session, "distribution.access");
    case "distribution-schedules":
      return !isClient(session) && hasPermission(session, "distribution.schedules.manage");
    case "tasks":
      return canExecuteTasks(session);
    case "maintenance-execution":
      return hasPermission(session, "maintenance.execute");
    case "governance":
      return hasPermission(session, "inspections.governance");
    case "vehicle":
      return hasPermission(session, "inspections.vehicle");
    case "maintenance-audit":
      return hasPermission(session, "maintenance.audit");
    case "governance-audit":
      return hasPermission(session, "inspections.audit.governance");
    case "vehicle-audit":
      return hasPermission(session, "inspections.audit.vehicle");
    case "vehicle-catalog-management":
      return hasPermission(session, "inspections.audit.vehicle");
    case "maintenance-planning":
      return hasPermission(session, "maintenance.assign");
    case "inspections-audit":
      if (
        normalizedPath(path).includes("governanca-preenchidas") ||
        normalizedPath(path).includes("/governanca")
      ) {
        return hasPermission(session, "inspections.audit.governance");
      }
      if (
        normalizedPath(path).includes("vistorias-preenchidas") ||
        normalizedPath(path).includes("/veicular")
      ) {
        return hasPermission(session, "inspections.audit.vehicle");
      }
      return Boolean(
        hasPermission(session, "inspections.audit.governance") ||
          hasPermission(session, "inspections.audit.vehicle"),
      );
    case "inspections-root-legacy":
      return Boolean(
        hasPermission(session, "inspections.governance") ||
          hasPermission(session, "inspections.vehicle"),
      );
    case "operations-root":
      return canAccessOperations(session);
    case "audit-root":
      return canAccessAudit(session);
    default:
      return true;
  }
}

function replacePathPrefix(path: string, legacyPrefix: string, canonicalPath: string) {
  const pathname = path.split(/[?#]/, 1)[0] || "/";
  const suffix = path.slice(pathname.length);
  return `${pathname.replace(new RegExp(`^${legacyPrefix}`, "i"), canonicalPath)}${suffix}`;
}

function hasWorkOrderRouteState(path: string) {
  const query = path.split("?", 2)[1]?.split("#", 1)[0];
  if (!query) return false;
  return Array.from(new URLSearchParams(query).keys()).some((key) =>
    key.startsWith("wo_"),
  );
}

function maintenanceLegacyTarget(session: CurrentSession, path: string) {
  const canExecute = hasPermission(session, "maintenance.execute");
  const canAudit = hasPermission(session, "maintenance.audit");
  if (canAudit && (!canExecute || hasWorkOrderRouteState(path))) {
    return CANONICAL_PRODUCT_PATHS.auditMaintenance;
  }
  return CANONICAL_PRODUCT_PATHS.operationMaintenance;
}

/** Canonicalizes only after authorization has been checked by the caller. */
export function canonicalPathFor(session: CurrentSession, path: string) {
  const pathname = normalizedPath(path);
  const distributionAlias = pathname.match(
    /^\/(df|sp)(?:\/(dashboard|distribuicao|historico))?\/?$/,
  );
  if (distributionAlias) {
    const tasks = hasPermission(session, "distribution.access");
    const target = tasks
      ? `/${distributionAlias[1]}/distribuicao/tarefas${
          distributionAlias[2] === "historico" ? "/historico" : ""
        }`
      : `/${distributionAlias[1]}/distribuicao/manutencao`;
    const source = pathname.endsWith("/") && pathname !== "/" ? pathname.slice(0, -1) : pathname;
    return replacePathPrefix(path, source, target);
  }
  const localeTasksLegacy = pathname.match(/^\/(df|sp)\/tarefas(?:\/historico)?\/?$/);
  if (localeTasksLegacy) {
    const locale = localeTasksLegacy[1];
    return replacePathPrefix(path, `/${locale}/tarefas`, `/${locale}/distribuicao/tarefas`);
  }
  if (
    matchesExactPath(pathname, "/dashboard") ||
    matchesExactPath(pathname, "/distribuicao") ||
    matchesExactPath(pathname, "/historico")
  ) {
    const target = `${distributionPath(session)}${
      matchesExactPath(pathname, "/historico") &&
      hasPermission(session, "distribution.access")
        ? "/historico"
        : ""
    }`;
    const source = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
    return replacePathPrefix(path, source, target);
  }
  if (pathname === "/operacao" || pathname === "/operacao/") {
    const suffix = path.slice((path.split(/[?#]/, 1)[0] || "/").length);
    return `${firstOperationPath(session)}${suffix}`;
  }
  if (pathname === "/auditoria" || pathname === "/auditoria/") {
    const suffix = path.slice((path.split(/[?#]/, 1)[0] || "/").length);
    return `${firstAuditPath(session)}${suffix}`;
  }
  const localeMaintenance = pathname.match(/^\/(df|sp)\/manutencao\/?$/);
  if (localeMaintenance) {
    const target = hasPermission(session, "maintenance.assign")
      ? `/${localeMaintenance[1]}/distribuicao/manutencao`
      : CANONICAL_PRODUCT_PATHS.auditMaintenance;
    return replacePathPrefix(path, `/${localeMaintenance[1]}/manutencao`, target);
  }
  if (matchesExactPath(pathname, "/staff/camareira")) {
    return replacePathPrefix(path, "/staff/camareira", CANONICAL_PRODUCT_PATHS.operationTasks);
  }
  if (matchesExactPath(pathname, "/staff/profissional")) {
    return replacePathPrefix(path, "/staff/profissional", CANONICAL_PRODUCT_PATHS.operationTasks);
  }
  if (matchesExactPath(pathname, "/manutencao/ordens")) {
    const target = maintenanceLegacyTarget(session, path);
    return replacePathPrefix(path, "/manutencao/ordens", target);
  }
  if (matchesExactPath(pathname, "/manutencao")) {
    const target = maintenanceLegacyTarget(session, path);
    return replacePathPrefix(path, "/manutencao", target);
  }
  if (matchesExactPath(pathname, "/inspecoes/governanca")) {
    return replacePathPrefix(path, "/inspecoes/governanca", CANONICAL_PRODUCT_PATHS.operationGovernance);
  }
  if (matchesExactPath(pathname, "/inspecoes/vistoria-veicular")) {
    return replacePathPrefix(path, "/inspecoes/vistoria-veicular", CANONICAL_PRODUCT_PATHS.operationVehicle);
  }
  if (pathname === "/inspecoes" || pathname === "/inspecoes/") {
    const target = hasPermission(session, "inspections.governance")
      ? CANONICAL_PRODUCT_PATHS.operationGovernance
      : CANONICAL_PRODUCT_PATHS.operationVehicle;
    return replacePathPrefix(path, "/inspecoes", target);
  }
  if (matchesExactPath(pathname, "/auditoria/dashboard")) {
    return replacePathPrefix(path, "/auditoria/dashboard", firstInspectionAuditPath(session));
  }
  if (matchesExactPath(pathname, "/auditoria/veicular")) {
    return replacePathPrefix(path, "/auditoria/veicular", CANONICAL_PRODUCT_PATHS.auditVehicle);
  }
  if (matchesExactPath(pathname, "/auditoria/vistorias-preenchidas")) {
    return replacePathPrefix(path, "/auditoria/vistorias-preenchidas", CANONICAL_PRODUCT_PATHS.auditVehicle);
  }
  if (matchesExactPath(pathname, "/auditoria/governanca-preenchidas")) {
    return replacePathPrefix(path, "/auditoria/governanca-preenchidas", CANONICAL_PRODUCT_PATHS.auditGovernance);
  }
  if (matchesExactPath(pathname, "/auditoria/controles")) {
    return replacePathPrefix(path, "/auditoria/controles", firstInspectionAuditPath(session));
  }
  if (matchesExactPath(pathname, "/admin/veiculos")) {
    return replacePathPrefix(
      path,
      "/admin/veiculos",
      CANONICAL_PRODUCT_PATHS.auditVehicleCatalog,
    );
  }
  if (matchesExactPath(pathname, "/auditoria/veiculos")) {
    return replacePathPrefix(
      path,
      "/auditoria/veiculos",
      CANONICAL_PRODUCT_PATHS.auditVehicleCatalog,
    );
  }
  if (matchesPrefix(pathname, "/auditoria/inspecoes")) {
    const target = pathname.includes("governanca")
      ? CANONICAL_PRODUCT_PATHS.auditGovernance
      : pathname.includes("veicular") || pathname.includes("vistorias")
        ? CANONICAL_PRODUCT_PATHS.auditVehicle
        : firstInspectionAuditPath(session);
    return replacePathPrefix(path, pathname, target);
  }
  return path;
}
